/**
 * MobileDrawer — Slide-in mobile navigation drawer.
 * Slides in from the right edge with a dimmed backdrop.
 * Used by both PublicLayout and AppShell for consistent mobile nav.
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  X, User, Users, Settings, HelpCircle,
  LogOut, FileText, Shield,
} from 'lucide-react';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ isOpen, onClose }) => {
  const { user, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const isPrimary = user?.role === 'primary';

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleNav = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleSignOut = () => {
    onClose();
    signOut();
    navigate('/');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`drawer-backdrop ${isOpen ? 'drawer-backdrop--open' : ''}`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className={`drawer-panel ${isOpen ? 'drawer-panel--open' : ''}`}>
        {/* Header */}
        <div className="drawer-header">
          {isAuthenticated ? (
            <div className="drawer-user">
              <span className="topbar-avatar">
                {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
              </span>
              <span className="drawer-user-name">{user?.displayName}</span>
            </div>
          ) : (
            <span className="drawer-title">Menu</span>
          )}
          <button className="drawer-close" onClick={onClose} aria-label="Close menu">
            <X size={20} />
          </button>
        </div>

        {/* Nav links */}
        <div className="drawer-nav">
          <Link to="/" className="drawer-link" onClick={onClose}>Home</Link>
          <Link to="/pricing" className="drawer-link" onClick={onClose}>Pricing</Link>
          <Link to="/faq" className="drawer-link" onClick={onClose}>FAQ</Link>
          <Link to="/contact" className="drawer-link" onClick={onClose}>Contact</Link>
          {isAuthenticated && (
            <Link to="/app" className="drawer-link drawer-link--accent" onClick={onClose}>
              Tribe Space
            </Link>
          )}
        </div>

        {/* Auth section */}
        {isAuthenticated ? (
          <>
            <div className="drawer-divider" />
            <div className="drawer-nav">
              <button className="drawer-link-btn" onClick={() => handleNav('/app/profile')}>
                <User size={16} /> Profile
              </button>
              <button className="drawer-link-btn" onClick={() => handleNav('/app/users')}>
                <Users size={16} /> My Tribe
              </button>
              {isPrimary && (
                <button className="drawer-link-btn" onClick={() => handleNav('/app/settings')}>
                  <Settings size={16} /> Settings
                </button>
              )}
              <button className="drawer-link-btn" onClick={() => handleNav('/app/support')}>
                <HelpCircle size={16} /> Support
              </button>
            </div>

            <div className="drawer-divider" />
            <div className="drawer-nav">
              <button className="drawer-link-btn" onClick={() => handleNav('/terms')}>
                <FileText size={16} /> Terms of Service
              </button>
              <button className="drawer-link-btn" onClick={() => handleNav('/privacy')}>
                <Shield size={16} /> Privacy Policy
              </button>
            </div>

            {user?.isAdmin && (
              <>
                <div className="drawer-divider" />
                <div className="drawer-nav">
                  <button className="drawer-link-btn" onClick={() => handleNav('/admin')}>
                    <Settings size={16} /> Admin Dashboard
                  </button>
                </div>
              </>
            )}

            <div className="drawer-divider" />
            <div className="drawer-nav">
              <button className="drawer-link-btn drawer-link-btn--danger" onClick={handleSignOut}>
                <LogOut size={16} /> Sign Out
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="drawer-divider" />
            <div className="drawer-actions">
              <Link to="/login" className="btn btn-secondary" onClick={onClose} style={{ width: '100%', textAlign: 'center' }}>
                Log In
              </Link>
              <Link to="/signup" className="btn btn-primary" onClick={onClose} style={{ width: '100%', textAlign: 'center' }}>
                Start Free Trial
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MobileDrawer;
